import React, { useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { I18n } from 'react-polyglot';
import { Provider, connect } from 'react-redux';
import { applyMiddleware, combineReducers, createStore } from 'redux';
import { BrowserRouter } from 'react-router-dom';
import thunk from 'redux-thunk';
import SiteFooter from './components/SiteFooter';
import SiteHeader from './components/SiteHeader';
import locales from './i18n';
import allFiltersReducer from './redux/allFiltersReducer';
import { recordLocalization } from './redux/googleAnalyticsActions';
import googleAnalyticsMiddleware from './redux/googleAnalyticsMiddleware';
import menuToggleReducer from './redux/menuToggleReducer';
import { removeOffline } from './redux/offlineBooksActions';
import { addSlimNotification } from './redux/slimNotificationActions';
import {
    changeLanguage,
    fetchMeWorkflow,
    getMenuList,
    logoutWorkflow,
} from './redux/userActions';
import userReducer from './redux/userReducer';
import { initViewport } from './redux/viewportActions';
import viewportReducer from './redux/viewportReducer';
import './scss/_bookends.scss';

const mapStateToProps = (state) => ({
    viewport: state.viewport,
    isLoggedIn: state.user.isLoggedIn,
    profile: state.user.profile,
    isFetchingMe: state.user.isFetchingMe,
    allFilters: state.allFilters,
    online: state.network?.online || true,
});

const mapDispatchToProps = {
    initViewport,
    logoutWorkflow,
    changeLanguage,
    fetchMeWorkflow,
    removeOffline,
    getMenuList,
    addSlimNotification,
    recordLocalization,
};

const SiteHeaderContainer = ({
    viewport,
    isLoggedIn,
    profile,
    isFetchingMe,
    online,
    initViewport,
    logoutWorkflow,
    changeLanguage,
    fetchMeWorkflow,
    getMenuList,
    addSlimNotification,
    recordLocalization,
}) => {
    const handleChangeLanguage = useCallback(
        (newLanguage) => {
            changeLanguage(newLanguage).then(() =>
                recordLocalization({
                    userEmail: profile.email,
                    newLanguage,
                })
            );

            const direction = ['ar', 'ur'].includes(localStorage.getItem('locale'))
                ? 'rtl'
                : 'ltr';
            document.querySelectorAll('#header, #footer').forEach((el) => {
                el.style.direction = direction;
            });
        },
        [changeLanguage, recordLocalization, profile]
    );

    useEffect(() => {
        initViewport();
        fetchMeWorkflow();
        getMenuList(localStorage.getItem('locale'));
    }, []);

    return (
        <div>
            <SiteHeader
                viewport={viewport}
                isLoggedIn={isLoggedIn}
                user={profile}
                logout={logoutWorkflow}
                isFetchingMe={isFetchingMe}
                offline={!online}
                addSlimNotification={addSlimNotification}
                changeLanguage={handleChangeLanguage}
            />
        </div>
    );
};

const SiteFooterContainer = ({
    viewport,
    isLoggedIn,
    online,
    initViewport,
    getMenuList,
}) => {
    useEffect(() => {
        initViewport();
        getMenuList(localStorage.getItem('locale'));
    }, []);

    return (
        <div>
            <SiteFooter
                viewport={viewport}
                isLoggedIn={isLoggedIn}
                shouldShowLanguageSelector={true}
                offline={!online}
            />
        </div>
    );
};

const ConnectedSiteHeaderContainer = connect(mapStateToProps, mapDispatchToProps)(SiteHeaderContainer);
const ConnectedSiteFooterContainer = connect(mapStateToProps, mapDispatchToProps)(SiteFooterContainer);

const middleware = [thunk];

const preloadedState = window.INITIAL_STATE;

if (process.env.REACT_APP_GA_PROPERTY_ID) {
    middleware.push(googleAnalyticsMiddleware);
}

const store = createStore(
    combineReducers({
        viewport: viewportReducer,
        user: userReducer,
        allFilters: allFiltersReducer,
        menuToggle: menuToggleReducer,
    }),
    preloadedState,
    applyMiddleware(...middleware)
);

const locale = localStorage.getItem('locale') || 'en';
const messages = locales[locale];

ReactDOM.render(
    <Provider store={store}>
        <BrowserRouter>
            <I18n locale={locale} messages={messages}>
                <ConnectedSiteHeaderContainer />
            </I18n>
        </BrowserRouter>
    </Provider>,
    document.getElementById('header')
);

ReactDOM.render(
    <Provider store={store}>
        <BrowserRouter>
            <I18n locale={locale} messages={messages}>
                <ConnectedSiteFooterContainer />
            </I18n>
        </BrowserRouter>
    </Provider>,
    document.getElementById('footer')
); 